import { BaseQuery, Unsubscribe } from '@chem-po/core'
import {
  AppName,
  getThreadMessageGroupCollectionPath,
  objectToArray,
  SelectedMessage,
  ThreadMessageGroup,
  ThreadType,
  WithId,
} from '@hb/shared'
import isEqual from 'lodash.isequal'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApp, useFetchData } from '../../contexts'
import { useTodayDate } from '../../hooks/useTodayDate'
import { useThreadsList } from '../contexts/useThreadsList'
import { FetchedGroup, UseThreadMessagesNew } from '../types'
import { useMessagesFromGroups } from './useMessagesFromGroups'

const BASE_GROUPS_MOBILE = 7
const BASE_GROUPS_WEB = 3
// no mobile cap: trimming displayed groups makes FlashList jump on scroll even
// with maintainVisibleContentPosition (verified on-device 2026-07); if the cap
// comes back it must comfortably exceed BASE_GROUPS_MOBILE + PREFETCH_GROUPS_MOBILE
// so a freshly paged-in batch of groups is never trimmed right back out
// const MAX_GROUPS_MOBILE = 15
const MAX_GROUPS_WEB = 6
// how many groups to keep buffered ahead of the visible window in each direction —
// mobile buffers more because a fetch can hand several groups to the list at once
const PREFETCH_GROUPS_MOBILE = 5
const PREFETCH_GROUPS_WEB = 3
// minimum messages a single fetchPreviousGroup/fetchNextGroup call should hand to
// the list on mobile: FlashList only re-arms onStartReached/onEndReached once the
// user scrolls back outside the threshold zone, so each page must be tall enough
// to push them out — one short group (a day with a lone message) is not
const MIN_MESSAGES_PER_PAGE_MOBILE = 10

const getNextGroupsQuery = (
  collectionPath: string,
  currMaxGroup: number,
  limit: number,
): BaseQuery<ThreadMessageGroup> => ({
  collection: collectionPath,
  filters: [
    {
      key: 'createdOn',
      operator: '>',
      value: currMaxGroup,
    },
  ],
  sort: {
    key: 'createdOn',
    direction: 'asc',
  },
  limit,
})

const getPreviousGroupsQuery = (
  collectionPath: string,
  currMinGroup: number,
  limit: number,
): BaseQuery<ThreadMessageGroup> => ({
  collection: collectionPath,
  filters: [
    {
      key: 'createdOn',
      operator: '<',
      value: currMinGroup,
    },
  ],
  sort: {
    key: 'createdOn',
    direction: 'desc',
  },
  limit,
})

const getRecentGroupsQuery = (
  collectionPath: string,
  appName: AppName,
): BaseQuery<ThreadMessageGroup> => ({
  collection: collectionPath,
  filters: [],
  sort: {
    key: 'createdOn',
    direction: 'desc',
  },
  limit: appName === 'mobile-app' ? BASE_GROUPS_MOBILE : BASE_GROUPS_WEB,
})

export const useNewThreadMessages = (
  uid: string | null,
  threadType: ThreadType,
  threadId: string | null,
): UseThreadMessagesNew => {
  const {
    widget: { searchResult },
  } = useThreadsList()
  const { subscribeToItem, fetchItem, fetchItems, useDocument } = useFetchData()
  const { appName } = useApp()
  const maxGroups = useRef(appName === 'mobile-app' ? null : MAX_GROUPS_WEB)
  const prefetchGroupCount = useRef(
    appName === 'mobile-app' ? PREFETCH_GROUPS_MOBILE : PREFETCH_GROUPS_WEB,
  )
  // web pages one group at a time (its window trims at MAX_GROUPS_WEB)
  const minMessagesPerPage = useRef(appName === 'mobile-app' ? MIN_MESSAGES_PER_PAGE_MOBILE : 1)
  const [selectedMessage, setSelectedMessage] = useState<SelectedMessage | null>(
    searchResult
      ? {
          messageGroupCreatedOn: searchResult.message.messageGroupCreatedOn,
          messageGroupId: searchResult.messageGroupId,
          messageId: searchResult.messageId,
          selectedOn: searchResult.selectedOn,
        }
      : null,
  )

  const prevSearchResultSelectedOn = useRef<number | null>(null)
  useEffect(() => {
    if (searchResult && searchResult.selectedOn > (prevSearchResultSelectedOn.current ?? 0)) {
      setSelectedMessage({
        messageGroupCreatedOn: searchResult.message.messageGroupCreatedOn,
        messageGroupId: searchResult.messageGroupId,
        messageId: searchResult.messageId,
        selectedOn: searchResult.selectedOn,
      })
      prevSearchResultSelectedOn.current = searchResult.selectedOn
    }
  }, [searchResult])

  const { todayUnix } = useTodayDate()
  const todayGroupId = useMemo(() => new Date(todayUnix).toISOString().split('T')[0], [todayUnix])

  const [hasPreviousGroup, setHasPreviousGroup] = useState(true)
  const [hasNextGroup, setHasNextGroup] = useState(true)
  const [loadingBaseGroups, setLoadingBaseGroups] = useState(false)
  const [loadingNextGroup, setLoadingNextGroup] = useState(false)
  const [loadingPreviousGroup, setLoadingPreviousGroup] = useState(false)

  const [fetchedGroups, setFetchedGroups] = useState<Record<string, WithId<ThreadMessageGroup>>>({})
  const collectionPath = useMemo(
    () => (threadId ? getThreadMessageGroupCollectionPath(threadType, threadId) : null),
    [threadType, threadId],
  )

  const latestGroup = useRef<number | null>(null)
  const earliestGroup = useRef<number | null>(null)
  // buffers of not-yet-displayed groups, ordered nearest-to-the-visible-window first
  // (previous: newest → oldest, next: oldest → newest); buffered groups are not
  // subscribed — they get a live subscription when they are displayed
  const prefetchedPreviousGroups = useRef<WithId<ThreadMessageGroup>[]>([])
  const prefetchedNextGroups = useRef<WithId<ThreadMessageGroup>[]>([])
  // set once a prefetch/fetch has seen the end of the collection in that direction —
  // there is nothing beyond the buffer tail left to fetch
  const noMorePreviousGroups = useRef(false)
  const noMoreNextGroups = useRef(false)
  // synchronous mirror of the fetchedGroups state — the group boundaries used by
  // the prefetch queries must not depend on when React runs state updaters
  const fetchedGroupsRef = useRef<Record<string, WithId<ThreadMessageGroup>>>({})
  const prefetchingPreviousGroup = useRef<Promise<void> | null>(null)
  const prefetchingNextGroup = useRef<Promise<void> | null>(null)
  // bumped whenever the thread view resets so in-flight prefetches can be discarded
  const resetGeneration = useRef(0)

  const prefetchPreviousGroup = useCallback(() => {
    const run = (): Promise<void> => {
      if (!collectionPath || !earliestGroup.current) return Promise.resolve()
      if (prefetchingPreviousGroup.current) return prefetchingPreviousGroup.current
      if (noMorePreviousGroups.current) return Promise.resolve()
      const buffered = prefetchedPreviousGroups.current
      const requested = prefetchGroupCount.current - buffered.length
      if (requested <= 0) return Promise.resolve()
      // top up beyond the furthest buffered group (or the visible window if empty)
      const boundary = buffered.length
        ? buffered[buffered.length - 1].createdOn
        : earliestGroup.current
      const generation = resetGeneration.current
      let fetched = false
      const promise = fetchItems(getPreviousGroupsQuery(collectionPath, boundary, requested))
        .then(prevGroups => {
          // the thread view was reset while this prefetch was in flight — discard
          if (generation !== resetGeneration.current) return
          fetched = true
          const combined = [...prefetchedPreviousGroups.current, ...prevGroups]
          prefetchedPreviousGroups.current = combined.slice(0, prefetchGroupCount.current)
          // only mark exhausted if the cap didn't drop any fetched groups
          if (prevGroups.length < requested && combined.length <= prefetchGroupCount.current) {
            noMorePreviousGroups.current = true
          }
          if (prefetchedPreviousGroups.current.length === 0) setHasPreviousGroup(false)
        })
        .finally(() => {
          if (prefetchingPreviousGroup.current === promise) prefetchingPreviousGroup.current = null
          // groups consumed while this fetch was in flight leave the buffer short,
          // and calls made during the flight were coalesced into this promise —
          // re-check the deficit now instead of waiting for the next consume
          if (fetched && generation === resetGeneration.current) run().catch(() => undefined)
        })
      prefetchingPreviousGroup.current = promise
      return promise
    }
    return run()
  }, [collectionPath, fetchItems])

  const prefetchNextGroup = useCallback(() => {
    const run = (): Promise<void> => {
      if (!collectionPath || !latestGroup.current) return Promise.resolve()
      if (prefetchingNextGroup.current) return prefetchingNextGroup.current
      if (noMoreNextGroups.current) return Promise.resolve()
      const buffered = prefetchedNextGroups.current
      const requested = prefetchGroupCount.current - buffered.length
      if (requested <= 0) return Promise.resolve()
      const boundary = buffered.length
        ? buffered[buffered.length - 1].createdOn
        : latestGroup.current
      const generation = resetGeneration.current
      let fetched = false
      const promise = fetchItems(getNextGroupsQuery(collectionPath, boundary, requested))
        .then(nextGroups => {
          // the thread view was reset while this prefetch was in flight — discard
          if (generation !== resetGeneration.current) return
          fetched = true
          const combined = [...prefetchedNextGroups.current, ...nextGroups]
          prefetchedNextGroups.current = combined.slice(0, prefetchGroupCount.current)
          // only mark exhausted if the cap didn't drop any fetched groups
          if (nextGroups.length < requested && combined.length <= prefetchGroupCount.current) {
            noMoreNextGroups.current = true
          }
          if (prefetchedNextGroups.current.length === 0) setHasNextGroup(false)
        })
        .finally(() => {
          if (prefetchingNextGroup.current === promise) prefetchingNextGroup.current = null
          // groups consumed while this fetch was in flight leave the buffer short,
          // and calls made during the flight were coalesced into this promise —
          // re-check the deficit now instead of waiting for the next consume
          if (fetched && generation === resetGeneration.current) run().catch(() => undefined)
        })
      prefetchingNextGroup.current = promise
      return promise
    }
    return run()
  }, [collectionPath, fetchItems])

  const groupSubscribers = useRef<Map<string, Unsubscribe>>(new Map())

  const subscribeToGroup = useCallback(
    (groupId: string) => {
      if (groupSubscribers.current.has(groupId)) return

      groupSubscribers.current.set(
        groupId,
        subscribeToItem<ThreadMessageGroup>(
          `${collectionPath}/${groupId}`,
          data => {
            if (!data) {
              return
            }
            // ignore late events for groups that have been trimmed out of the window
            const existing = fetchedGroupsRef.current[groupId]
            if (!existing) return
            // a subscription's initial snapshot re-delivers the data we just
            // fetched — skip no-op updates so displaying N groups doesn't queue
            // N full list rebuilds right after a page-in. Compare fields rather
            // than the whole object: fetched groups carry an injected id, while
            // snapshot payloads may not
            if (existing.createdOn === data.createdOn && isEqual(existing.messages, data.messages))
              return
            fetchedGroupsRef.current = { ...fetchedGroupsRef.current, [groupId]: data }
            setFetchedGroups(fetchedGroupsRef.current)
          },
          error => {
            console.error(error)
          },
        ),
      )
    },
    [collectionPath, subscribeToItem],
  )

  const unsubscribeFromGroup = useCallback((groupId: string) => {
    const unsubscribe = groupSubscribers.current.get(groupId)
    if (unsubscribe) {
      unsubscribe()
      groupSubscribers.current.delete(groupId)
    }
  }, [])

  const unsubscribeFromAllGroups = useCallback(() => {
    groupSubscribers.current.forEach(unsubscribe => unsubscribe())
    groupSubscribers.current.clear()
    prefetchedPreviousGroups.current = []
    prefetchedNextGroups.current = []
    prefetchingPreviousGroup.current = null
    prefetchingNextGroup.current = null
    noMorePreviousGroups.current = false
    noMoreNextGroups.current = false
    resetGeneration.current += 1
    latestGroup.current = null
    earliestGroup.current = null
    prevSearchResultSelectedOn.current = null
    fetchedGroupsRef.current = {}
    setFetchedGroups({})
    setHasPreviousGroup(true)
    setHasNextGroup(true)
  }, [])

  useEffect(
    () => () => {
      unsubscribeFromAllGroups()
    },
    [unsubscribeFromAllGroups],
  )

  const updateFetchedGroups = useCallback(
    (groups: WithId<ThreadMessageGroup>[], direction: 'next' | 'previous') => {
      groups.forEach(group => subscribeToGroup(group.id))
      const newGroups = { ...fetchedGroupsRef.current }
      groups.forEach(group => {
        newGroups[group.id] = group
      })

      let sortedGroups = objectToArray(newGroups).sort((a, b) => a.createdOn - b.createdOn)
      let removed: WithId<ThreadMessageGroup>[] = []
      if (maxGroups.current && sortedGroups.length > maxGroups.current) {
        removed =
          direction === 'next'
            ? sortedGroups.slice(0, sortedGroups.length - maxGroups.current)
            : sortedGroups.slice(maxGroups.current, sortedGroups.length)

        if (removed.length > 0) {
          if (direction === 'next') setHasPreviousGroup(true)
          else setHasNextGroup(true)
        }

        // trimmed groups slot back into the prefetch buffer on the side they fell
        // off, nearest-to-the-window first, so scrolling back is instant
        if (direction === 'next') {
          // removed are the oldest displayed groups (ascending) — nearest is last
          const combined = [...removed].reverse().concat(prefetchedPreviousGroups.current)
          prefetchedPreviousGroups.current = combined.slice(0, prefetchGroupCount.current)
          // dropping buffered groups means there are fetchable groups beyond the tail
          if (combined.length > prefetchGroupCount.current) noMorePreviousGroups.current = false
        } else {
          // removed are the newest displayed groups (ascending) — nearest is first
          const combined = removed.concat(prefetchedNextGroups.current)
          prefetchedNextGroups.current = combined.slice(0, prefetchGroupCount.current)
          if (combined.length > prefetchGroupCount.current) noMoreNextGroups.current = false
        }
        removed.forEach(group => {
          unsubscribeFromGroup(group.id)
          delete newGroups[group.id]
        })
        sortedGroups = objectToArray(newGroups).sort((a, b) => a.createdOn - b.createdOn)
      }
      latestGroup.current = sortedGroups[sortedGroups.length - 1]?.createdOn ?? null
      earliestGroup.current = sortedGroups[0]?.createdOn ?? null
      fetchedGroupsRef.current = newGroups
      setFetchedGroups(newGroups)
      return {
        added: groups,
        removed,
      }
    },
    [subscribeToGroup, unsubscribeFromGroup],
  )

  const messageSelectedOn = useRef<number | null>(null)
  const fetchBaseGroups = useCallback(async () => {
    if (!collectionPath || !threadId) {
      unsubscribeFromAllGroups()
      console.error('Collection reference not found')
      return
    }
    if (selectedMessage) {
      if (messageSelectedOn.current && selectedMessage.selectedOn <= messageSelectedOn.current)
        return
      messageSelectedOn.current = selectedMessage.selectedOn
      // the selected message's group is already loaded — no refetch needed, the
      // list can scroll to and highlight it directly
      if (groupSubscribers.current.has(selectedMessage.messageGroupId)) return
      unsubscribeFromAllGroups()
      setLoadingBaseGroups(true)
      setLoadingNextGroup(true)
      setLoadingPreviousGroup(true)
      setHasNextGroup(true)
      setHasPreviousGroup(true)
      const prevGroupQuery = getPreviousGroupsQuery(
        collectionPath,
        selectedMessage.messageGroupCreatedOn,
        1,
      )
      const nextGroupQuery = getNextGroupsQuery(
        collectionPath,
        selectedMessage.messageGroupCreatedOn,
        1,
      )
      const promises = [
        fetchItems(prevGroupQuery),
        fetchItem<ThreadMessageGroup>(`${collectionPath}/${selectedMessage.messageGroupId}`).then(
          group => [group],
        ),
        fetchItems(nextGroupQuery),
      ]
      const allGroups = (await Promise.all(promises))
        .flat()
        .filter(Boolean) as WithId<ThreadMessageGroup>[]

      updateFetchedGroups(allGroups, 'previous')
      prefetchPreviousGroup()
      prefetchNextGroup()

      setLoadingNextGroup(false)
      setLoadingPreviousGroup(false)
      setLoadingBaseGroups(false)
    } else {
      messageSelectedOn.current = null
      unsubscribeFromAllGroups()
      setLoadingBaseGroups(true)
      setHasNextGroup(false)
      const recentGroupsQuery = getRecentGroupsQuery(collectionPath, appName)
      const recentGroups = await fetchItems(recentGroupsQuery)
      updateFetchedGroups(recentGroups, 'previous')
      prefetchPreviousGroup()
      setLoadingBaseGroups(false)
    }
  }, [
    collectionPath,
    selectedMessage,
    unsubscribeFromAllGroups,
    updateFetchedGroups,
    prefetchPreviousGroup,
    prefetchNextGroup,
    fetchItems,
    threadId,
    fetchItem,
    appName,
  ])

  const { data: todayData, loading: loadingToday } = useDocument<ThreadMessageGroup>(
    collectionPath ?? '',
    hasNextGroup ? null : todayGroupId,
  )
  useEffect(() => {
    fetchBaseGroups()
  }, [fetchBaseGroups])

  useEffect(
    () => () => {
      unsubscribeFromAllGroups()
    },
    [unsubscribeFromAllGroups],
  )

  // take groups off the front of a prefetch buffer until the page holds at least
  // minMessagesPerPage messages — handing the list a single short group would
  // prepend/append too little content to re-arm FlashList's reached triggers
  const takePageFromBuffer = useCallback((buffer: WithId<ThreadMessageGroup>[]) => {
    const taken: WithId<ThreadMessageGroup>[] = []
    let messageCount = 0
    for (const group of buffer) {
      if (taken.length > 0 && messageCount >= minMessagesPerPage.current) break
      taken.push(group)
      messageCount += Object.keys(group.messages).length
    }
    return taken
  }, [])

  const fetchNextGroup = useCallback(async () => {
    if (!collectionPath || !threadId) {
      throw new Error('Collection reference not found')
    }
    if (!prefetchedNextGroups.current.length && prefetchingNextGroup.current) {
      // a prefetch is already in flight — wait for it instead of re-querying
      setLoadingNextGroup(true)
      await prefetchingNextGroup.current
      setLoadingNextGroup(false)
    }
    let updated: FetchedGroup | null = null
    const buffered = takePageFromBuffer(prefetchedNextGroups.current)
    if (buffered.length) {
      const remaining = prefetchedNextGroups.current.slice(buffered.length)
      prefetchedNextGroups.current = remaining
      if (!remaining.length && noMoreNextGroups.current) setHasNextGroup(false)
      updated = updateFetchedGroups(buffered, 'next')
    } else {
      if (noMoreNextGroups.current) {
        setHasNextGroup(false)
        return null
      }
      const currMaxGroup = latestGroup.current
      if (currMaxGroup === null) return null
      setLoadingNextGroup(true)
      const nextGroups = await fetchItems(
        getNextGroupsQuery(collectionPath, currMaxGroup, prefetchGroupCount.current),
      )
      setLoadingNextGroup(false)
      if (nextGroups.length < prefetchGroupCount.current) noMoreNextGroups.current = true
      if (nextGroups.length === 0) {
        setHasNextGroup(false)
        return null
      }
      updated = updateFetchedGroups(nextGroups, 'next')
      if (noMoreNextGroups.current) setHasNextGroup(false)
    }
    prefetchNextGroup()
    return updated
  }, [
    collectionPath,
    updateFetchedGroups,
    fetchItems,
    prefetchNextGroup,
    takePageFromBuffer,
    threadId,
  ])

  const fetchPreviousGroup = useCallback(async () => {
    if (!collectionPath || !threadId) {
      throw new Error('Collection reference not found')
    }
    if (!prefetchedPreviousGroups.current.length && prefetchingPreviousGroup.current) {
      // a prefetch is already in flight — wait for it instead of re-querying
      setLoadingPreviousGroup(true)
      await prefetchingPreviousGroup.current
      setLoadingPreviousGroup(false)
    }
    let updated: FetchedGroup | null = null
    const buffered = takePageFromBuffer(prefetchedPreviousGroups.current)
    if (buffered.length) {
      const remaining = prefetchedPreviousGroups.current.slice(buffered.length)
      prefetchedPreviousGroups.current = remaining
      if (!remaining.length && noMorePreviousGroups.current) setHasPreviousGroup(false)
      updated = updateFetchedGroups(buffered, 'previous')
    } else {
      if (noMorePreviousGroups.current) {
        setHasPreviousGroup(false)
        return null
      }
      const currMinGroup = earliestGroup.current
      if (currMinGroup === null) return null
      setLoadingPreviousGroup(true)
      const previousGroups = await fetchItems(
        getPreviousGroupsQuery(collectionPath, currMinGroup, prefetchGroupCount.current),
      )
      setLoadingPreviousGroup(false)
      if (previousGroups.length < prefetchGroupCount.current) noMorePreviousGroups.current = true
      if (previousGroups.length === 0) {
        setHasPreviousGroup(false)
        return null
      }
      updated = updateFetchedGroups(previousGroups, 'previous')
      if (noMorePreviousGroups.current) setHasPreviousGroup(false)
    }
    prefetchPreviousGroup()
    return updated
  }, [
    collectionPath,
    updateFetchedGroups,
    fetchItems,
    prefetchPreviousGroup,
    takePageFromBuffer,
    threadId,
  ])

  const allMessages = useMessagesFromGroups(uid, threadType, threadId, fetchedGroups, todayData)

  return {
    data: allMessages,
    selectedMessage,
    selectMessage: setSelectedMessage,
    loadingToday,
    loadingBaseGroups,
    loadingNextGroup,
    loadingPreviousGroup,
    fetchNextGroup,
    fetchPreviousGroup,
    hasPreviousGroup,
    fetchBaseGroups,
    hasNextGroup,
  }
}
